import {
  AgentPayoutSchema,
  EarningsLedgerEntrySchema,
  RecruiterAgentBackrefSchema,
  RecruiterProfileSchema,
} from '@kilat/shared';

import { createRepo } from './_base.js';

/**
 * Sprint 85 — recruiter PWA surfaces inside the agent-pwa shell.
 *
 * Tenant isolation is enforced upstream by firestore.rules:
 *   match /recruiters/{recruiterId} {
 *     allow read: if isThisRecruiter(recruiterId) || isSuperAdmin();
 *     ...
 *   }
 * so the live subscriptions below only succeed for the signed-in
 * recruiter's own doc + sub-collections.
 */

/** Recruiter profile doc at `recruiters/{recruiterId}`. */
export const recruitersRepo = createRepo({
  path: 'recruiters',
  schema: RecruiterProfileSchema,
  name: 'recruiters',
});

/**
 * `recruiters/{recruiterId}/agents/{agentId}` — back-ref written by
 * `agentBootstrap` (Sprint 84) on recruiter-attributed signups, patched
 * by `commissionAccrue` (Sprint 83) on first/subsequent downstream paid
 * invoices. Drives the recruiter dashboard + `/recruiter/agents` list.
 *
 * Recruiters can't read `agents/{aid}` directly (rules block cross-doc
 * reads outside the recruiter's own subtree), so this denormalised
 * back-ref is the only source for "who did I sign up, what's their
 * status, how much have they earned me".
 */
export function recruiterAgentsBackrefRepoFor(recruiterId: string) {
  return createRepo({
    path: `recruiters/${recruiterId}/agents`,
    schema: RecruiterAgentBackrefSchema,
    name: `recruiters/${recruiterId}/agents`,
  });
}

/**
 * `recruiters/{recruiterId}/earningsLedger/{entryId}` — recruiter's
 * own copy of the same ledger shape as the agent one. Written by:
 *   - `commissionAccrue` (kind: 'accrued') — recurring + first-invoice splits
 *   - `commissionClawback` (kind: 'clawed_back') — symmetric reverse on refund
 *   - super-admin adjustments (kind: 'adjustment')
 *   - payout run (kind: 'paid')
 *
 * `basis.shareKind` distinguishes recurring vs first-invoice entries
 * in the rendered ledger row (so a recruiter sees "Recurring share"
 * vs "Onboarding share" rather than two opaque "Accrued" pills).
 */
export function recruiterEarningsLedgerRepoFor(recruiterId: string) {
  return createRepo({
    path: `recruiters/${recruiterId}/earningsLedger`,
    schema: EarningsLedgerEntrySchema,
    name: `recruiters/${recruiterId}/earningsLedger`,
  });
}

/**
 * `recruiters/{recruiterId}/payouts/{payoutId}` — recruiter payout batches,
 * written by the SAME `executePayoutBatch` Cloud Function as agent payouts
 * with `kind: 'recruiter'` (Sprint 97/98; the super-admin `/superadmin/payouts`
 * "Pay recruiters" tab triggers them). Identical `AgentPayoutSchema` shape.
 * Read-only from the client; rules scope it via `isThisRecruiter`.
 */
export function recruiterPayoutsRepoFor(recruiterId: string) {
  return createRepo({
    path: `recruiters/${recruiterId}/payouts`,
    schema: AgentPayoutSchema,
    name: `recruiters/${recruiterId}/payouts`,
  });
}
